import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { ShieldCheck, Sparkles, Lock, Heart, CheckCircle2 } from "lucide-react";

function Shell({ children }) {
  return (
    <div className="bg-[#FDFBF7] min-h-screen">
      <header className="sticky top-0 z-30 bg-[#FDFBF7]/90 backdrop-blur border-b border-[#EADFCF]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/manajodi-logo.png" alt="ManaJodi logo" className="h-9 w-9 rounded-xl" width="36" height="36" />
            <span className="font-serif font-bold text-xl text-[#58111A]">Mana<span className="text-[#C59B27]">Jodi</span></span>
          </Link>
          <nav className="flex items-center gap-3 text-sm">
            <Link to="/login" className="text-[#781826] font-semibold hidden sm:inline">Log in</Link>
            <Link to="/register" className="btn-primary text-sm px-4 py-2 rounded-full">Create Free Profile</Link>
          </nav>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">{children}</main>
      <Footer />
    </div>
  );
}

function Hero({ eyebrow, h1, lead }) {
  return (
    <div className="mb-8">
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9E7B1B] mb-3">{eyebrow}</div>
      <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#58111A] leading-tight">{h1}</h1>
      <p className="text-[15px] sm:text-base text-[#605255] mt-4 leading-relaxed">{lead}</p>
      <div className="mt-6 flex flex-wrap gap-2">
        <Link to="/register" className="btn-primary px-5 py-2.5 rounded-full">Create Your Free Profile</Link>
        <Link to="/how-it-works" className="btn-outline px-5 py-2.5 rounded-full">How It Works</Link>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, title, children }) {
  return (
    <div className="flex gap-3">
      <span className="w-9 h-9 rounded-lg bg-[#FAF3E0] flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-[#C59B27]" /></span>
      <div><h3 className="font-semibold text-[#261A1C]">{title}</h3><p className="text-sm text-[#605255] mt-0.5">{children}</p></div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-serif text-2xl font-bold text-[#58111A] mb-3">{title}</h2>
      <div className="text-[15px] text-[#4A3B3D] leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

const attribution = (
  <p className="mt-12 pt-6 border-t border-[#EADFCF] text-sm text-[#8A6D71]">
    ManaJodi is a matrimonial platform by <a href="https://vs-solutions.in" target="_blank" rel="noopener" className="text-[#781826] font-medium underline">Vijay Software Solutions Pvt Ltd</a>. Your Jodi. Your Journey.
  </p>
);

const trust = (
  <div className="grid sm:grid-cols-2 gap-4 mt-8">
    <Feature icon={ShieldCheck} title="Verified Profiles">Identity, education and employment verification build trust before you connect.</Feature>
    <Feature icon={Sparkles} title="Smart Matching">Compatibility-led recommendations based on your genuine partner preferences.</Feature>
    <Feature icon={Lock} title="Privacy First">Protected photos, controlled contact sharing and secure, connection-gated chat.</Feature>
    <Feature icon={Heart} title="Family Friendly">Involve family where it matters, with privacy you stay in control of.</Feature>
  </div>
);

function webpageLd(name, description, path) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name, description, url: "https://manajodi.net" + path,
    isPartOf: { "@type": "WebSite", name: "ManaJodi", url: "https://manajodi.net" },
    publisher: { "@type": "Organization", name: "ManaJodi", parentOrganization: { "@type": "Organization", name: "Vijay Software Solutions Pvt Ltd", url: "https://vs-solutions.in" } },
  };
}

export function TeluguMatrimony() {
  return (
    <Shell>
      <Seo title="Telugu Matrimony | Find Your Life Partner | ManaJodi" path="/telugu-matrimony"
        description="Telugu matrimony on ManaJodi — discover verified Telugu bride and groom profiles from Andhra Pradesh and Telangana. Preference-based matching, privacy-first and family-friendly."
        jsonLd={webpageLd("Telugu Matrimony — ManaJodi", "Verified Telugu matrimonial profiles with preference-based matching.", "/telugu-matrimony")} />
      <Hero eyebrow="Telugu Matrimony" h1="Telugu Matrimony, done the trusted way"
        lead="ManaJodi helps Telugu families and individuals from Andhra Pradesh and Telangana find compatible, verified life partners — with privacy, smart matching and respect for tradition." />
      {trust}
      <Section title="Verified Telugu bride & groom profiles">
        <p>Browse genuine Telugu matrimonial profiles with clear preferences around community, mother tongue, location and values. Every profile can carry verification badges so you connect with confidence.</p>
      </Section>
      <Section title="Preference-based matchmaking">
        <p>Set your partner preferences — age, community, education, profession and lifestyle — and ManaJodi surfaces compatible matches, explaining why each is a good fit.</p>
      </Section>
      <Section title="Start your journey">
        <p><Link to="/register" className="text-[#781826] font-semibold underline">Create your free profile</Link> or read our <Link to="/matrimony-guide" className="text-[#781826] font-semibold underline">matrimony guide</Link> for tips on finding the right match.</p>
      </Section>
      {attribution}
    </Shell>
  );
}

export function IndianMatrimony() {
  return (
    <Shell>
      <Seo title="Indian Matrimony | Verified Matrimonial Profiles | ManaJodi" path="/indian-matrimony"
        description="Indian matrimony on ManaJodi — a trusted online matrimonial platform with verified profiles, AI-assisted compatibility, privacy controls and secure communication."
        jsonLd={webpageLd("Indian Matrimony — ManaJodi", "Trusted Indian matrimonial platform with verified profiles.", "/indian-matrimony")} />
      <Hero eyebrow="Indian Matrimony" h1="A trusted Indian matrimonial platform"
        lead="Find a life partner across communities and regions of India with ManaJodi — verified profiles, compatibility-led matching and privacy you control." />
      {trust}
      <Section title="Why families choose ManaJodi">
        <p>From first search to a meaningful connection, ManaJodi focuses on trust and compatibility rather than noise. Profiles are moderated, photos can be protected, and contact details are shared only with your consent.</p>
      </Section>
      <Section title="Explore more">
        <p><Link to="/telugu-matrimony" className="text-[#781826] font-semibold underline">Telugu Matrimony</Link> · <Link to="/life-partner" className="text-[#781826] font-semibold underline">Find a Life Partner</Link> · <Link to="/matrimony-guide" className="text-[#781826] font-semibold underline">Matrimony Guide</Link></p>
      </Section>
      {attribution}
    </Shell>
  );
}

export function LifePartner() {
  return (
    <Shell>
      <Seo title="Find Your Life Partner | ManaJodi Matrimonial Platform" path="/life-partner"
        description="Find your life partner with ManaJodi. Verified matrimonial profiles, smart partner-preference search, secure chat and privacy-first connections."
        jsonLd={webpageLd("Find Your Life Partner — ManaJodi", "Find a compatible life partner with verified profiles and smart matching.", "/life-partner")} />
      <Hero eyebrow="Life Partner Search" h1="Find your life partner, with you in control"
        lead="ManaJodi brings together verified profiles, compatibility-led matching and privacy-first communication so you can focus on meaningful connections." />
      {trust}
      <Section title="How ManaJodi helps you find the one">
        <ul className="list-disc pl-5 space-y-1">
          <li>Set clear partner preferences and get compatible matches.</li>
          <li>Connect through secure, connection-gated chat.</li>
          <li>Share contact details only when you choose to.</li>
          <li>Verify profiles for added trust.</li>
        </ul>
      </Section>
      {attribution}
    </Shell>
  );
}

export function MatrimonyGuide() {
  return (
    <Shell>
      <Seo title="How ManaJodi Works | Matrimony Guide to Finding a Life Partner" path="/matrimony-guide"
        description="A practical matrimony guide: how to create a better matrimonial profile, use partner preferences, stay safe and find a compatible life partner on ManaJodi."
        jsonLd={webpageLd("Matrimony Guide — ManaJodi", "Practical guide to finding a compatible life partner.", "/matrimony-guide")} />
      <Hero eyebrow="Matrimony Guide" h1="Your guide to finding the right match"
        lead="Simple, practical advice to help you present yourself well, search effectively and connect safely on your matrimonial journey." />
      <Section title="1. Build a strong, honest profile">
        <p>Add a clear photo, a warm two-line introduction and accurate details. Complete profiles receive far more genuine interest.</p>
      </Section>
      <Section title="2. Use partner preferences effectively">
        <p>Be specific but realistic about what matters — community, values, education and lifestyle — so matching surfaces the right people.</p>
      </Section>
      <Section title="3. Stay safe and private">
        <p>Keep conversations on-platform at first, protect your photos, and share contact details only when you're comfortable. See our <Link to="/safety" className="text-[#781826] font-semibold underline">safety centre</Link>.</p>
      </Section>
      {attribution}
    </Shell>
  );
}

const POSTS = [
  ["How to Create a Better Matrimonial Profile", "A complete profile with a clear photo and honest intro earns more genuine interest."],
  ["How to Choose the Right Life Partner", "Focus on shared values and long-term compatibility over surface details."],
  ["Important Questions to Discuss Before Marriage", "Family, finances, careers and expectations — talk early and openly."],
  ["How to Stay Safe on Matrimonial Platforms", "Protect your privacy, verify profiles and keep early chats on-platform."],
  ["How to Use Partner Preferences Effectively", "Be specific yet realistic so matching surfaces the right people."],
  ["How Verified Profiles Help Build Trust", "Verification badges give families confidence before connecting."],
];

export function Blog() {
  return (
    <Shell>
      <Seo title="ManaJodi Blog | Matrimony Tips, Safety & Partner Search" path="/blog"
        description="ManaJodi blog — practical articles on matrimonial profiles, partner preferences, privacy and safely finding a compatible life partner."
        jsonLd={webpageLd("ManaJodi Blog", "Matrimony tips, safety and partner-search guidance.", "/blog")} />
      <Hero eyebrow="ManaJodi Blog" h1="Matrimony tips & guidance"
        lead="Genuinely useful articles to help you create a great profile, search smartly and stay safe on your journey to finding a life partner." />
      <div className="mt-8 space-y-4">
        {POSTS.map(([title, excerpt]) => (
          <article key={title} className="mj-card p-5">
            <h2 className="font-serif text-lg font-semibold text-[#58111A]">{title}</h2>
            <p className="text-sm text-[#605255] mt-1">{excerpt}</p>
            <Link to="/matrimony-guide" className="inline-flex items-center gap-1 text-sm text-[#781826] font-semibold mt-2">Read the guide <CheckCircle2 className="w-4 h-4 text-[#C59B27]" /></Link>
          </article>
        ))}
      </div>
      {attribution}
    </Shell>
  );
}
